import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { Star } from 'lucide-react';

const StarRating = ({ 
  value = 0, 
  onChange, 
  maxStars = 5, 
  size = 48, 
  disabled = false,
  showValue = true,
  className 
}) => {
  const [hoverValue, setHoverValue] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  // 计算当前显示的评分值（悬浮时显示悬浮值，否则显示实际值）
  const displayValue = isHovering ? hoverValue : value;

  // 处理鼠标进入星星
  const handleMouseEnter = useCallback((starIndex) => {
    if (disabled) return;
    setIsHovering(true);
    setHoverValue(starIndex + 1);
  }, [disabled]);

  // 处理鼠标离开星星区域
  const handleMouseLeave = useCallback(() => {
    if (disabled) return;
    setIsHovering(false);
    setHoverValue(0);
  }, [disabled]);

  // 处理鼠标在星星上的移动（用于半星检测）
  const handleMouseMove = useCallback((_, starIndex) => {
    if (disabled) return;
    setHoverValue(starIndex + 1); // 只允许整数
  }, [disabled]);

  // 处理点击星星
  const handleClick = useCallback((_, starIndex) => {
    if (disabled) return;
    const clickValue = starIndex + 1;
    onChange && onChange(clickValue);
  }, [disabled, onChange]);

  // 渲染单个星星
  const renderStar = (index) => {
    const starValue = index + 1;
    const isHalfStar = false;
    const isFullStar = displayValue >= starValue;
    
    return (
      <StarContainer
        key={index}
        onMouseEnter={() => handleMouseEnter(index)}
        onMouseMove={(e) => handleMouseMove(e, index)}
        onClick={(e) => handleClick(e, index)}
        disabled={disabled}
        className={className}
      >
        <Star
          size={size}
          fill={isFullStar ? '#fbbf24' : '#9ca3af'}
          stroke={isFullStar ? '#fbbf24' : '#9ca3af'}
          strokeWidth={0}
        />
        
      </StarContainer>
    );
  };

  return (
    <StarRatingContainer>
      <StarRatingWrapper onMouseLeave={handleMouseLeave}>
        {Array.from({ length: maxStars }, (_, index) => renderStar(index))}
      </StarRatingWrapper>
      {showValue && (
        <RatingValue>
          {displayValue > 0 ? `${displayValue}星` : '未评分'}
        </RatingValue>
      )}
    </StarRatingContainer>
  );
};

// 样式组件
const StarRatingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const StarRatingWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StarContainer = styled.div`
  position: relative;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  padding: 2px;
  border-radius: 4px;
  opacity: ${props => props.disabled ? 0.6 : 1};

  &:hover {
    transform: ${props => props.disabled ? 'none' : 'scale(1.1)'};
  }
`;

const HalfStarOverlay = styled.div`
  position: absolute;
  top: 2px;
  left: 2px;
  width: 50%;
  height: calc(100% - 4px);
  overflow: hidden;
  pointer-events: none;
`;

const RatingValue = styled.div`
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
`;

export default StarRating; 